// =============================================================================
// constants.ts — Constantes compartilhadas entre servidor e cliente.
// O servidor é AUTORITATIVO: tudo aqui é a verdade única da simulação.
// =============================================================================

export const TICK_RATE = 25;            // ticks de simulação por segundo (servidor)
export const DT = 1 / TICK_RATE;        // delta fixo de simulação (40ms)
export const SNAPSHOT_EVERY = 2;        // snapshot enviado a cada 2 ticks (12.5Hz) — cliente interpola
export const INTERP_DELAY_MS = 120;     // atraso de interpolação no cliente
// Buffer seco (snapshot atrasado/perdido)? O cliente extrapola a posição pela
// velocidade dos 2 últimos snapshots por até este tempo, em vez de congelar e
// "pular" quando o pacote chega. Cobre ~1-2 snapshots perdidos (intervalo 80ms).
export const EXTRAP_MAX_MS = 120;       // teto de extrapolação (dead reckoning)

// Interest culling: cada cliente só recebe entidades num raio ao redor do
// próprio jogador (com folga sobre a meia-diagonal de telas comuns). Bosses
// são exceção e vão sempre. Em telas gigantes (>3400px de largura) pode haver
// pop-in no canto — troca aceitável pra cortar a banda com a horda lotada.
export const AOI_RADIUS = 1700;

export const GAME_DURATION = 20 * 60;   // 20 minutos de sobrevivência (em segundos)

// Modo infinito (issue #85): sobreviveu à apuração? O Brasil continua — e piora.
// Ao aceitar, a dificuldade dá um salto imediato; o teto da horda e o dano por
// tempo já seguem crescendo sozinhos pelas curvas do #84 (maxEnemiesAt etc).
export const INFINITE_DIFF_MULT = 1.5;        // salto imediato de dificuldade ao aceitar
export const INFINITE_DMG_PER_MIN = 0.06;     // +6% de dano inimigo por minuto extra
export const INFINITE_BOSS_INTERVAL = 120;    // reprise de boss aleatório a cada 2min

// Ranking (issue #88): além dos pontos por abate (campo `pontos` de cada
// inimigo em shared/enemies.ts), sobreviver também pontua — por segundo VIVO
// (caído/zumbi não conta; o relógio congelado da urna/pausa também não).
export const SCORE_ALIVE_PER_SEC = 5;

// Eleições com seed (issues #101 e #97): números dos modificadores do dia.
// Horário Eleitoral Gratuito: a horda 2x mais rápida na abertura da campanha.
export const ELEICAO_HORARIO_DURACAO = 300;        // primeiros 5 minutos
export const ELEICAO_HORARIO_SPEED_MULT = 2;       // velocidade da horda no período
// Dinheiro na Cueca: maletas em dobro, mas a CPI da BET fareja mais alto
export const ELEICAO_CUECA_CHEST_MULT = 2;         // multiplica CHEST_MAX/INITIAL e a taxa
export const ELEICAO_CUECA_CPI_WEIGHT_MULT = 3;    // peso extra do prêmio 'cpi' na roleta

// Arena: a "Praça dos Três Poderes Expandida"
export const ARENA_W = 4000;
export const ARENA_H = 4000;

export const MAX_PLAYERS = 4;
export const MIN_PLAYERS = 1;           // dá pra sofrer sozinho também
export const ROOM_CODE_LEN = 6;

export const PLAYER_RADIUS = 22;
export const PICKUP_BASE_RADIUS = 70;   // raio base do "ímã de votos"
export const GEM_RADIUS = 10;

// Revive cooperativo
export const DOWNED_BLEEDOUT = 30;      // segundos até o caído virar zumbi político
export const REVIVE_RANGE = 90;         // distância pra reviver aliado
export const REVIVE_TIME = 3;           // segundos parado perto do corpo
export const REVIVE_HP_FRAC = 0.5;      // revive com 50% do HP

// Zumbi político: o aliado que virou "cidadão de bem" ou "militante"
export const ZOMBIE_HP_FRAC = 0.6;
export const ZOMBIE_SPEED = 110;
export const ZOMBIE_DAMAGE = 10;

// Urna aberta = JOGO CONGELADO: ninguém toma dano enquanto escolhe upgrade.
// Se o indeciso estourar o timer, o servidor vota por ele (bem Brasil isso).
export const URNA_TIME = 12;            // segundos pra escolher na urna

// Poderes da mesa diretora da urna (issue #100): cargas POR JOGADOR por
// partida, validadas no servidor. O auto-voto do timeout NUNCA gasta carga.
export const URNA_REROLL_CHARGES = 2;   // Recontagem de Votos: re-sorteia as 3 opções
export const URNA_BANISH_CHARGES = 2;   // Cassação de Mandato: remove a opção do pool até o fim
export const URNA_SKIP_CHARGES = 1;     // Abstenção: recusa o level up
export const URNA_SKIP_HEAL_FRAC = 0.1; // compensação da abstenção: cura 10% do HP máximo
export const URNA_SKIP_SCORE = 150;     // compensação da abstenção pra jogador CAÍDO: pontos de
                                        // ranking (o revive sobrescreve o HP, cura não serviria)

// Emenda de Relator (issue #93): chance REAL e FIXA de cada oferta da urna
// vir dourada, com o efeito daquele upgrade multiplicado. Publicada no card —
// sem near-miss fabricado, sem chance dinâmica escondida.
export const URNA_GOLDEN_CHANCE = 0.08; // 8% por oferta (sorteio do servidor)
export const URNA_GOLDEN_MULT = 1.6;    // incremento ~1.6x (ex.: +40% em vez de +25%)

// Reconexão: a vaga fica guardada — político nunca perde o mandato fácil
export const RECONNECT_GRACE = 120;     // segundos de tolerância p/ reconectar em jogo

// REELEIÇÃO (issue #91): depois do game over a sala fica 'ended' esse tempo,
// esperando alguém da chapa apertar REELEIÇÃO antes do cartório arquivar tudo
export const ROOM_ENDED_TTL = 300;      // segundos até a sala encerrada ser destruída

// Malas de dinheiro espalhadas pelo mapa: encostou, abriu o Cassino da Maleta
export const CHEST_MAX = 5;             // máximo de malas simultâneas no mapa
export const CHEST_INITIAL = 3;         // malas já espalhadas no início
export const CHEST_INTERVAL = 30;       // segundos entre novas malas
export const CHEST_RADIUS = 30;         // raio de coleta (encostou, abriu)
export const CHEST_HEAL_FRAC = 0.4;     // prêmio "vida": cura 40% do HP máximo
export const CHEST_XP_FRAC = 0.3;       // prêmio "voto": 30% do XP do nível atual

// Cassino da Maleta (CPI da BET): a roleta gira com o MUNDO CONGELADO (como a
// urna) e o prêmio só aplica quando o card fecha — senão o HUD entrega antes.
export const CASINO_SPIN_TIME = 3.2;    // segundos de roleta girando
export const CASINO_RESULT_TIME = 2.2;  // segundos exibindo o resultado
export const CASINO_FORO_TIME = 8;      // duração da imunidade do prêmio "foro"
export const CASINO_CPI_XP_FRAC = 0.15; // fração do XP confiscada pela CPI

// Caixa 2 (issues #90/#94): moeda PERSISTENTE que dropa de elites e bosses
// como pickup no chão e é creditada na conta ao fim da partida (vitória ou
// derrota), junto com um bônus por minuto sobrevivido. Os votos vitalícios
// (todo XP coletado) e os marcos de desbloqueio moram em src/shared/meta.ts.
//
// Calibrado pra progressão VS-like: morte parcial paga o 1º nível em poucas
// runs; vitória forte (~1000 elites coletados) enche o Gabinete em ~15–40
// wins — elite drop baixo evita o full-clear teórico virar free max em 1 noite.
export const CAIXA2_ELITE_DROP = 1;       // maço dropado por elite abatido
export const CAIXA2_BOSS_COINS = 5;       // maços espalhados por boss abatido
export const CAIXA2_BOSS_COIN_VALUE = 2;  // valor de cada maço do boss
export const CAIXA2_BONUS_PER_MIN = 2;    // bônus de fim de partida por minuto
export const CAIXA2_BONUS_VICTORY = 40;   // bônus por vencer a apuração (20min)
export const MAX_COINS = 200;             // teto de maços no chão (funde como os votos)

// Crise institucional: mais de 1 boss vivo ao MESMO TEMPO agrava tudo. Cada
// boss extra acelera a horda inteira e aumenta o dano de todos os bosses —
// deixar escândalo acumulando sai caro. Com teto, senão empilhar até o
// 8 de Janeiro vira injogável.
export const MULTIBOSS_SPEED_PER_EXTRA = 0.15; // +15% de velocidade da horda por boss extra
export const MULTIBOSS_DMG_PER_EXTRA = 0.25;   // +25% de dano dos bosses por boss extra
export const MULTIBOSS_SPEED_CAP = 1.6;        // teto do multiplicador de velocidade
export const MULTIBOSS_DMG_CAP = 2.0;          // teto do multiplicador de dano

// Limites de sanidade da simulação. O teto de inimigos agora é DINÂMICO
// (maxEnemiesAt, lá embaixo) — este aqui é o teto DURO que segura o servidor
// não importa quanto tempo a partida dure.
export const MAX_ENEMIES_HARD = 2000;
export const MAX_GEMS = 500;
export const MAX_PROJECTILES = 400;
export const MAX_ALLIES = 14;           // aliados corruptos invocados (total da sala)

// Ducking de trilha (issue #118): enquanto uma voz gravada fala (seleção de
// personagem hoje; bordão de boss amanhã), tudo que não é voz cai pra esta
// fração do volume e volta quando a fala termina. Compartilhada porque o
// evento de fala do boss nasce no servidor — nível único pros dois lados.
export const VOICE_DUCK_LEVEL = 0.15;

// XP = "Votos". Curva de level: quanto mais alto o cargo, mais caro o voto.
export function xpForLevel(level: number): number {
  return Math.floor(6 + (level - 1) * 4 + Math.pow(level - 1, 1.65));
}

// Escala de dificuldade das hordas por minuto decorrido
export function difficultyAt(elapsed: number): number {
  const m = elapsed / 60;
  return 1 + m * 0.35 + Math.pow(m / 6, 2);
}

// Dano dos inimigos cresce com o relógio: ~2x aos 20 minutos. SEM teto de
// propósito — um futuro modo infinito conta com a curva seguindo depois disso.
export function enemyDamageScaleAt(elapsed: number): number {
  const m = elapsed / 60;
  return 1 + m * 0.05;
}

// Velocidade também sobe, mas de leve e COM teto: inimigo mais rápido que
// jogador vira partida indesviável. +2% por minuto, parando em +45%.
export function enemySpeedScaleAt(elapsed: number): number {
  const m = elapsed / 60;
  return Math.min(1 + m * 0.02, 1.45);
}

// Teto de inimigos VIVOS cresce com o tempo, estilo Vampire Survivors:
// ~300 no início, ~1500 aos 20 min, subindo devagar até bater no teto duro.
export function maxEnemiesAt(elapsed: number): number {
  const m = elapsed / 60;
  return Math.min(Math.floor(300 + m * 60), MAX_ENEMIES_HARD);
}

// Multiplicador de TEMPO da taxa de spawn: sem ele o teto novo nunca é
// alcançado. Começa em 1x (início igual ao de hoje) e explode no fim de
// partida — quem segura o servidor é o maxEnemiesAt, não a taxa.
export function spawnRateMultAt(elapsed: number): number {
  const m = elapsed / 60;
  return 1 + Math.pow(m / 8, 2);
}
